<template>
  <Card class="lg:col-span-2 overflow-hidden border-border shadow-sm" :class="{ 'fixed inset-0 z-[100] rounded-none p-8': isChartFullscreen }">
    <CardHeader class="flex flex-row items-center justify-between space-y-0 pb-4">
      <CardTitle class="text-xl font-bold">Répartition et Rentabilité</CardTitle>
      <div class="flex items-center gap-3">
        <div class="flex bg-muted p-1 rounded-lg">
          <button 
            class="px-3 py-1 text-xs font-medium rounded-md transition-all"
            :class="displayMode === 'chart' ? 'bg-background shadow-sm text-foreground font-bold' : 'text-muted-foreground hover:text-foreground'"
            @click="setDisplayMode('chart')"
          >Graphique</button>
          <button 
            class="px-3 py-1 text-xs font-medium rounded-md transition-all"
            :class="displayMode === 'table' ? 'bg-background shadow-sm text-foreground font-bold' : 'text-muted-foreground hover:text-foreground'"
            @click="setDisplayMode('table')"
          >Tableau</button>
        </div>
        <Button variant="ghost" size="sm" class="h-8 w-8 p-0" @click="toggleFullscreen">
          <Maximize2 v-if="!isChartFullscreen" class="h-4 w-4" />
          <X v-else class="h-4 w-4" />
        </Button>
      </div>
      <!-- Background indicator -->
      <div v-if="isRefreshing" class="absolute top-2 right-12 flex items-center gap-2 bg-card/80 backdrop-blur-sm px-2 py-1 rounded-full border border-border shadow-sm text-foreground">
        <div class="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-ping"></div>
        <span class="text-[9px] font-bold text-muted-foreground uppercase tracking-widest">Calcul...</span>
      </div>
    </CardHeader>

    <CardContent>
      <div v-if="displayMode === 'chart'" class="space-y-4">
        <div class="flex items-center gap-2 text-xs text-muted-foreground bg-muted/50 p-2 rounded-md w-fit">
          <Info class="h-3.5 w-3.5" />
          <span>Astuce : Cliquez sur une catégorie dans la légende pour la masquer.</span>
        </div>
        <div class="relative h-[350px]">
          <Line v-if="chartData && chartData.labels && chartData.labels.length" :key="chartKey" :data="chartData" :options="chartOptions" />
          <div v-else class="flex items-center justify-center h-full text-muted-foreground italic">
            Aucun mouvement réalisé à tracer.
          </div>
        </div>
      </div>

      <div v-else class="pt-2">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Catégorie</TableHead>
              <TableHead class="text-right">Versement</TableHead>
              <TableHead class="text-right">Encaissement</TableHead>
              <TableHead class="text-right">Solde Net</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow v-for="cat in categorySums" :key="cat.name">
              <TableCell class="font-medium text-zinc-700">{{ cat.name }}</TableCell>
              <TableCell class="text-right text-red-600 font-semibold tabular-nums" :class="{ 'blur-sm select-none': isAmountsHidden }">
                -{{ formatAmount(cat.payment) }}€
              </TableCell>
              <TableCell class="text-right text-emerald-500 font-semibold tabular-nums" :class="{ 'blur-sm select-none': isAmountsHidden }">
                +{{ formatAmount(cat.receipt) }}€
              </TableCell>
              <TableCell class="text-right font-bold tabular-nums" :class="[cat.net > 0 ? 'text-emerald-500' : (cat.net < 0 ? 'text-red-500' : ''), { 'blur-sm select-none': isAmountsHidden }]">
                {{ cat.net > 0 ? '+' : '' }}{{ formatAmount(cat.net) }}€
              </TableCell>
            </TableRow>
            <TableRow v-if="!categorySums || !categorySums.length">
              <TableCell colspan="4" class="text-center text-muted-foreground py-8 italic">Aucun mouvement réalisé.</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </CardContent>
  </Card>
</template>

<script setup>
import { ref } from 'vue'
import { Line } from 'vue-chartjs'
import { Maximize2, X, Info } from 'lucide-vue-next'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table'
import { formatAmount } from '@/lib/utils'

defineProps({
  chartData: { type: Object, required: true },
  chartOptions: { type: Object, required: true },
  chartKey: { type: Number, required: true },
  isChartFullscreen: { type: Boolean, required: true },
  displayMode: { type: String, required: true }, // 'chart' or 'table'
  categorySums: { type: Array, required: true },
  isAmountsHidden: { type: Boolean, default: false },
  isRefreshing: { type: Boolean, default: false }
})

const emit = defineEmits(['toggle-fullscreen', 'update:displayMode'])

const toggleFullscreen = () => emit('toggle-fullscreen')
const setDisplayMode = (mode) => emit('update:displayMode', mode)
</script>
